import React from 'react';

// Form + subcomponents. Mirrors react-bootstrap: Form.Group(controlId) wires id/htmlFor
// to Control/Label; Control/Select/Check/Text/Range + Control.Feedback.
const ControlIdContext = React.createContext<string | undefined>(undefined);

const inputBase =
  'tw-block tw-w-full tw-rounded tw-border tw-bg-surface tw-px-3 tw-py-1.5 tw-text-content tw-transition ' +
  'placeholder:tw-text-content-muted focus:tw-outline-none focus:tw-border-line-focus focus:tw-shadow-focus ' +
  'disabled:tw-bg-surface-alt disabled:tw-opacity-70';

export interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  validated?: boolean;
}
interface FormType extends React.FC<FormProps> {
  Group: React.ForwardRefExoticComponent<React.HTMLAttributes<HTMLDivElement> & { controlId?: string } & React.RefAttributes<HTMLDivElement>>;
  Label: React.FC<React.LabelHTMLAttributes<HTMLLabelElement> & { column?: boolean }>;
  Control: React.FC<ControlProps> & { Feedback: React.FC<FeedbackProps> };
  Select: React.FC<SelectProps>;
  Check: React.ForwardRefExoticComponent<CheckProps & React.RefAttributes<HTMLInputElement>>;
  Text: React.FC<React.HTMLAttributes<HTMLElement>>;
  Range: React.FC<React.InputHTMLAttributes<HTMLInputElement>>;
}

export const Form = (({ validated, className = '', children, ...rest }: FormProps) => (
  <form className={className} noValidate={validated} {...rest}>{children}</form>
)) as FormType;

Form.Group = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { controlId?: string }>(
  ({ controlId, className = '', children, ...rest }, ref) => (
    <ControlIdContext.Provider value={controlId}>
      <div ref={ref} className={`tw-mb-3 ${className}`} {...rest}>{children}</div>
    </ControlIdContext.Provider>
  ),
);

Form.Label = ({ column, className = '', htmlFor, children, ...rest }) => {
  const id = React.useContext(ControlIdContext);
  return (
    <label htmlFor={htmlFor || id} className={`tw-mb-1 tw-inline-block tw-font-medium tw-text-content-secondary ${className}`} {...rest}>
      {children}
    </label>
  );
};

interface ControlProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  as?: 'input' | 'textarea';
  isInvalid?: boolean;
  isValid?: boolean;
  plaintext?: boolean;
  size?: 'sm' | 'lg';
  rows?: number;
}
const ControlBase: React.FC<ControlProps> = ({ as = 'input', isInvalid, isValid, plaintext, size, className = '', id, ...rest }) => {
  const ctxId = React.useContext(ControlIdContext);
  const sizeCls = size === 'sm' ? 'tw-py-1 tw-text-sm' : size === 'lg' ? 'tw-py-2.5 tw-text-lg' : '';
  const state = isInvalid ? 'tw-border-danger' : isValid ? 'tw-border-success' : 'tw-border-line-input';
  // plaintext = read-only text with no input chrome (react-bootstrap Form.Control plaintext).
  const base = plaintext ? 'tw-block tw-w-full tw-bg-transparent tw-px-0 tw-py-1.5 tw-text-content tw-border-0' : `${inputBase} ${state}`;
  const cls = `${base} ${sizeCls} ${className}`;
  if (as === 'textarea') {
    const { rows, ...taRest } = rest as any;
    return <textarea id={id || ctxId} rows={rows || 3} className={cls} {...taRest} />;
  }
  return <input id={id || ctxId} className={cls} {...rest} />;
};
interface FeedbackProps extends React.HTMLAttributes<HTMLDivElement> { type?: 'valid' | 'invalid'; }
const Feedback: React.FC<FeedbackProps> = ({ type = 'invalid', className = '', children, ...rest }) => (
  <div className={`tw-mt-1 tw-text-sm ${type === 'invalid' ? 'tw-text-danger' : 'tw-text-success'} ${className}`} {...rest}>{children}</div>
);
Form.Control = Object.assign(ControlBase, { Feedback });

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  isInvalid?: boolean;
  size?: 'sm' | 'lg';
  htmlSize?: number; // maps to the native <select size> (visible rows), like react-bootstrap
}
Form.Select = ({ isInvalid, size, htmlSize, className = '', id, children, ...rest }) => {
  const ctxId = React.useContext(ControlIdContext);
  const sizeCls = size === 'sm' ? 'tw-py-1 tw-text-sm' : size === 'lg' ? 'tw-py-2.5 tw-text-lg' : '';
  return (
    <select id={id || ctxId} size={htmlSize} className={`${inputBase} ${sizeCls} ${isInvalid ? 'tw-border-danger' : 'tw-border-line-input'} ${className}`} {...rest}>
      {children}
    </select>
  );
};

interface CheckProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: 'checkbox' | 'radio' | 'switch';
  label?: React.ReactNode;
  inline?: boolean;
  isInvalid?: boolean;
}
Form.Check = React.forwardRef<HTMLInputElement, CheckProps>(
  ({ type = 'checkbox', label, inline, isInvalid, className = '', id, ...rest }, ref) => {
    const ctxId = React.useContext(ControlIdContext);
    const inputType = type === 'switch' ? 'checkbox' : type;
    const cid = id || ctxId;
    return (
      <div className={`${inline ? 'tw-inline-flex tw-mr-4' : 'tw-flex'} tw-items-center tw-gap-2 ${className}`}>
        <input
          ref={ref}
          id={cid}
          type={inputType}
          className={`tw-h-4 tw-w-4 tw-rounded tw-border-line-input tw-text-primary focus:tw-shadow-focus ${isInvalid ? 'tw-border-danger' : ''}`}
          {...rest}
        />
        {label != null && <label htmlFor={cid} className="tw-text-content">{label}</label>}
      </div>
    );
  },
);

Form.Text = ({ className = '', children, ...rest }) => (
  <small className={`tw-mt-1 tw-block tw-text-sm tw-text-content-muted ${className}`} {...rest}>{children}</small>
);

Form.Range = ({ className = '', id, ...rest }) => {
  const ctxId = React.useContext(ControlIdContext);
  return <input id={id || ctxId} type="range" className={`tw-w-full tw-accent-primary ${className}`} {...rest} />;
};

export default Form;
